import { FullMetadata } from 'firebase/storage'
import { GCSFileMetadataStatus, GCSFileMetadataStatusIndicator } from './gcs-file-self-assessment'

/**
 * File Processing Stage Enum
 * Defines what stage of file processing is required for a feature.
 */
export enum FileProcessingStage {
  // Returns DONE as soon as file is uploaded (no processing required)
  UPLOADED = 'uploaded',
  // Returns DONE when markdown conversion is complete (includes initial chunk embeddings)
  MARKDOWN_CONVERTED = 'markdown_converted',
  // Returns DONE when sentence processing is complete (JSON files ready, sentence embeddings uploaded but not indexed)
  SENTENCE_PROCESSING_COMPLETE = 'sentence_processing_complete',
  // Returns DONE after waiting for Pinecone to finish indexing embeddings
  EMBEDDINGS_INDEXED = 'embeddings_indexed',
}

/**
 * Determine GCS File Status
 * This function attempts to determine the overall usability and status of the GCS file.
 *
 * LLM Note: This function is the only source of truth for a GCS / Google Cloud Storage file's status.
 *
 * Based on a plethora of metadata fields set at different times by different upload processing microservices,
 * we attempt to provide the user with a reliable status indicator for whether the file can be utilized.
 *
 * Order:
 * - Return any KNOWN status indicator when encountered as soon as possible.
 *   Known status indicators are metadata that tell us FOR SURE that we can determine the file status.
 * - The fileReadyAtStage parameter determines when to return DONE - earlier stages return DONE sooner,
 *   allowing features to use files before the full pipeline completes. Defaults to EMBEDDINGS_INDEXED
 *   which waits for the entire pipeline including Pinecone indexing.
 *
 * RACE CONDITIONS:
 * - Some of the processing steps are done conditionally or in parallel and can finish at different times,
 *   before the file is complete. For example, sentence_success and processing_success can finish at different times
 *   and a single one does not guarantee the file is complete.
 *
 * PROCESSING:
 * - If the file does not fall into a DONE or ERROR status, we can assume the file is still processing.
 *
 * PROCESSING EXPIRATION:
 * - Files that are still processing for more than 15 minutes are considered ERROR
 *
 * @param fileMetadata - The GCS file metadata
 * @param fileReadyAtStage - What stage of processing the file must reach to be ready (see FileProcessingStage enum)
 * @returns {GCSFileMetadataStatus} The file status
 */
export function determineGcsFileStatus(props: { fileMetadata: FullMetadata; fileReadyAtStage?: FileProcessingStage }): GCSFileMetadataStatus {
  const { fileMetadata, fileReadyAtStage = FileProcessingStage.EMBEDDINGS_INDEXED } = props

  // Defensive check: if fileMetadata is null/undefined, return error status
  if (!fileMetadata) {
    return {
      status: GCSFileMetadataStatusIndicator.ERROR,
      errorMessage: 'File metadata is invalid or missing.',
    }
  }

  // Calculate age once
  const timeCreated = new Date(fileMetadata.timeCreated)
  const now = new Date()
  const ageInMinutes = (now.getTime() - timeCreated.getTime()) / 1000 / 60
  const customMetadata = fileMetadata.customMetadata
  const processingErrorMetadata = customMetadata?.processing_error?.trim()
  const processSuccessMetadata = customMetadata?.process_success?.trim()
  const sentenceSuccessMetadata = customMetadata?.sentence_success?.trim()
  const hasSentencesMetadata = customMetadata?.has_sentences?.trim()

  //===================================>
  // ERROR CONDITIONS: Check for Error scenarios
  // Is this naming confusing? These variables are meant to indicate whether the literal True / False value of the string
  // NOT to suggest that the underlying process itself is successful or not... because even though that would make sense, that's not how it works.
  // For the logic in this system, we need to know the String value of the metadata is or is not "True" or "False"
  //
  // Legacy files usually wrote "True" / "False". The OCR reject metadata currently writes lowercase "true" / "false".
  // Keep this plain and literal so it is obvious we are checking metadata strings, not abstract process state.
  const processingErrorIsTrue: boolean = processingErrorMetadata === 'True' || processingErrorMetadata === 'true'
  const errorStringExists: boolean = typeof customMetadata?.error == 'string' && customMetadata.error.trim() != ''
  const processSuccessIsFalse: boolean = processSuccessMetadata === 'False' || processSuccessMetadata === 'false'
  const sentenceSuccessIsFalse: boolean = sentenceSuccessMetadata === 'False' || sentenceSuccessMetadata === 'false'

  if (processingErrorIsTrue || errorStringExists || processSuccessIsFalse || sentenceSuccessIsFalse) {
    let errorMessage = 'There was an error processing this file.'

    const backendErrorMessage = customMetadata?.error?.trim()
    const ocrRejectReason = customMetadata?.ocr_reject_reason?.trim()
    const ocrLimitType = customMetadata?.ocr_limit_type?.trim()
    const ocrLimitValueRaw = customMetadata?.ocr_limit_value?.trim()

    if (ocrRejectReason === 'page_limit_exceeded' && ocrLimitType === 'page_count' && ocrLimitValueRaw) {
      const ocrLimitValue = Number.parseInt(ocrLimitValueRaw, 10)

      if (!Number.isNaN(ocrLimitValue)) {
        errorMessage = `Requires OCR and exceeds ${ocrLimitValue}-page OCR limit.`
      } else if (backendErrorMessage) {
        errorMessage = backendErrorMessage
      }
    } else if (ocrRejectReason === 'size_limit_exceeded' && ocrLimitType === 'file_size_bytes' && ocrLimitValueRaw) {
      const ocrLimitValue = Number.parseInt(ocrLimitValueRaw, 10)

      if (!Number.isNaN(ocrLimitValue)) {
        const megabytes = ocrLimitValue / (1024 * 1024)
        const formattedMegabytes = Number.isInteger(megabytes) ? String(megabytes) : megabytes.toFixed(1).replace(/\.0$/, '')
        errorMessage = `Requires OCR and exceeds ${formattedMegabytes} MB OCR limit.`
      } else if (backendErrorMessage) {
        errorMessage = backendErrorMessage
      }
    } else if (ocrRejectReason && backendErrorMessage) {
      errorMessage = backendErrorMessage
    }

    return {
      status: GCSFileMetadataStatusIndicator.ERROR,
      errorMessage,
    }
  }

  //===========================================>
  // DONE CONDITION: Check for UPLOADED stage
  // Files that only need to exist (no processing required) can be used immediately after upload.
  // - If we have fileMetadata, the file exists
  // - As long as there are no errors, the file is ready to use
  if (fileReadyAtStage === FileProcessingStage.UPLOADED) {
    return {
      status: GCSFileMetadataStatusIndicator.DONE,
    }
  }

  //===================================>
  // DONE CONDITION: Legacy File Check
  // Legacy files do not have timestamps, but do have process_success.
  // If process_success is true and there is no timestamp, and it's older than 60 mins, it's a legacy file that is done.
  const isLegacySuccess =
    (processSuccessMetadata === 'True' || processSuccessMetadata === 'true') && !customMetadata?.process_success_timestamp && ageInMinutes > 60

  if (isLegacySuccess) {
    return {
      status: GCSFileMetadataStatusIndicator.DONE,
    }
  }

  //===================================>
  // SHARED VARIABLES: Used across multiple DONE conditions
  const isMarkdownProcessingComplete = processSuccessMetadata === 'True' || processSuccessMetadata === 'true'
  const isSentenceProcessingComplete = sentenceSuccessMetadata === 'True' || sentenceSuccessMetadata === 'true'
  const hasSentences: boolean = hasSentencesMetadata === 'True' || hasSentencesMetadata === 'true'

  //===========================================>
  // DONE CONDITION: Check for MARKDOWN_CONVERTED stage
  // process_success metadata is True when the markdown has been saved and the initial embeddings have been sent to Pinecone.
  //
  // For functions that ONLY require markdown and no additional processing, return DONE here.
  //
  // WARNING: Pinecone can take some time to index, so this does not mean the file is ready for RAG, only for
  //          functions that read the entire markdown file and DO NOT need to query pinecone.
  if (fileReadyAtStage === FileProcessingStage.MARKDOWN_CONVERTED && isMarkdownProcessingComplete) {
    return {
      status: GCSFileMetadataStatusIndicator.DONE,
    }
  }

  //===========================================>
  // DONE CONDITION: Check for SENTENCE_PROCESSING_COMPLETE stage
  // Sentence processing generates JSON mapping files, generates embeddings, and upserts embeddings to Pinecone.
  // - When sentence_success=True, JSON files are ready but Pinecone indexing needs an additional waiting period
  // - Features like Med Chron need the JSON files but don't query Pinecone, so we can skip the wait
  if (fileReadyAtStage === FileProcessingStage.SENTENCE_PROCESSING_COMPLETE && isMarkdownProcessingComplete && hasSentences && isSentenceProcessingComplete) {
    return {
      status: GCSFileMetadataStatusIndicator.DONE,
    }
  }

  //=======================================>
  // DONE CONDITION: Validate Success Timestamps against Buffer Delay
  // The below "DONE" conditions are predicated on enough time going by that Pinecone has been able to index the content.
  // -This requires a delay after the "DONE" timestamps
  //
  // There will only be a sentence_process_timestamp if has_sentences == "True"
  const processSuccessTimestamp = customMetadata?.process_success_timestamp
  const sentenceSuccessTimestamp = customMetadata?.sentence_process_timestamp

  // Determine if there is a timestamp to evaluate for DONE
  const timestampToEvaluateForDone = getTimestampToEvaluateAsDone(hasSentences, sentenceSuccessTimestamp, processSuccessTimestamp)
  if (timestampToEvaluateForDone) {
    // Check if the eligible timestamp is past the waiting period
    const isPastWaitingPeriod = successTimeExceedsWaitingPeriod(timestampToEvaluateForDone)

    if (isPastWaitingPeriod) {
      return {
        status: GCSFileMetadataStatusIndicator.DONE,
      }
    }
  }

  //=========== NO MORE DONE CONDITIONS ====================>

  //====================================================>
  // ERROR CONDITION: Processing Time Expired
  // If we reach this stage without any other condition being activated,
  // it means there must have been some sort of error that prevented the
  // metadata from being updated.
  //
  // Mark as errored if the age of the file is older than 15 minutes
  if (ageInMinutes > 15) {
    return {
      status: GCSFileMetadataStatusIndicator.ERROR,
      errorMessage: 'File processing timed out.',
    }
  }

  //========= CATCHALL =========>
  // Everything else can be considered processing
  return {
    status: GCSFileMetadataStatusIndicator.PROCESSING,
  }
}

/**
 * Success Time Exceeds Waiting Period
 *
 * The timestamp of file upload success is too optimistic. Pinecone database currently reports
 * successful indexing even before the indexing is not actually complete. For large files, this
 * causes the file to report being completed processing before it's actually done processing.
 *
 * THIS IS A BANDAID function to add an arbitrary delay to the file upload time, to see if enough
 * time has passed AFTER the file has marked itself as successfully processed.
 *
 * @param successTimestampSeconds (SECONDS) is the timestamp of when the file metadata reported upload was successful
 * @returns {boolean} whether the time delta is greater than X seconds past the process_success_timestamp
 */
export function successTimeExceedsWaitingPeriod(successTimestampSeconds: number): boolean {
  // Change this variable to adjust the time delay / delta
  // THIS IS BASED ON OBSERVED REAL-WORLD DELAYS IN PROCESSING STAGES
  // 30 SECONDS SHOULD ELIMINATE ANY INCONSISTENCY IN USER EXPERIENCE
  const waitingPeriod = 30

  // Now in seconds (to match process_success_timestamp)
  const now = Math.floor(Date.now() / 1000)

  // Get the difference between now and the process_success_timestamp
  const timeDiffSeconds = now - successTimestampSeconds

  // If the time difference is greater than the delta, return true (successfully processed)
  const isExceedingWaitingPeriod = timeDiffSeconds >= waitingPeriod
  // console.log(`Time diff: ${timeDiffSeconds} seconds, waiting period: ${waitingPeriod} seconds, isExceedingWaitingPeriod: ${isExceedingWaitingPeriod}`)

  return isExceedingWaitingPeriod
}

/**
 * Get Timestamp to Evaluate As Done
 * Just returns the best timestamp to evaluate, does not perform any calculations
 *
 * Based on the conditions in this function, certain timestamps should be prioritized.
 *
 * returns null if there is no valid timestamp
 */
function getTimestampToEvaluateAsDone(
  hasSentences: boolean,
  sentenceSuccessTimestamp: string | undefined,
  processSuccessTimestamp: string | undefined
): number | null {
  // There is no timestamp to evaluate
  if (!sentenceSuccessTimestamp && !processSuccessTimestamp) {
    return null
  }

  // If hasSentences is false, return the process_success_timestamp for evaluation
  if (!hasSentences) {
    if (!processSuccessTimestamp) {
      return null
    }

    const processSuccessTimestampSeconds = parseInt(processSuccessTimestamp, 10)
    return processSuccessTimestampSeconds
  }

  // If hasSentences is true, evaluate both timestamps and select the later timestamp
  if (hasSentences) {
    // Both timestamps are needed at this stage to be evaluatable for DONE
    if (!sentenceSuccessTimestamp || !processSuccessTimestamp) {
      return null
    }

    const sentenceSuccessTimestampSeconds = parseInt(sentenceSuccessTimestamp, 10)
    const processSuccessTimestampSeconds = parseInt(processSuccessTimestamp, 10)

    // The later timestamp is the one we should use. Return the later timestamp.
    return Math.max(sentenceSuccessTimestampSeconds, processSuccessTimestampSeconds)
  }

  return null
}
